import {
  hasInjectionContext,
  inject,
  ref,
  toValue,
  watchEffect,
  type MaybeRef,
} from "vue";
import type Ably from "ably";
import { useAblyChannelRegistry, CHANNELS } from "./ably-channel-registry";
import { ablySdkInjectionKey } from "./use-ably-sdk";

export function useSocketsCountPresence({
  channelSlug,
}: {
  channelSlug: MaybeRef<string>;
}) {
  const registry = useAblyChannelRegistry();
  const ablySdk = hasInjectionContext()
    ? inject(ablySdkInjectionKey, null)
    : null;
  const connectionsCount = ref(0);
  const isLoaded = ref(false);

  watchEffect(() => {
    if (!ablySdk || ablySdk.connectionState.value === "connected") return;
    connectionsCount.value = 0;
    isLoaded.value = false;
  });

  watchEffect((onCleanup) => {
    const currentChannelSlug = toValue(channelSlug);
    connectionsCount.value = 0;
    isLoaded.value = false;

    if (!currentChannelSlug) return;

    // Reader-only descriptor — blocks any future engineer from accidentally
    // calling acquirePresencePusher on this channel (compile error).
    const handle = registry.subscribePresence({
      descriptor: CHANNELS.presenceCount,
      channelArgs: [currentChannelSlug],
    });

    // Occupancy is a meta-topic on a regular subscribe — NOT a presence operation.
    const unsubscribe = handle.subscribe(
      "[meta]occupancy",
      (msg: Ably.Message) => {
        const data = msg.data as Ably.ChannelOccupancy;
        connectionsCount.value = data.metrics.subscribers ?? 0;
        isLoaded.value = true;
      },
    );

    onCleanup(() => {
      unsubscribe();
      handle.release();
    });
  });

  return { connectionsCount, isLoaded };
}
